import { useState } from "react";
import {
  UseFormClearErrors,
  UseFormSetError,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";
import { toast } from "react-toastify";

import {
  CODE_VERIFICATION_CHANNELS,
  CODE_VERIFICATION_SCOPES,
  COUNTRY_CODES,
  DEFAULT_CODE_LENGTH,
  INITIAL_VERIFICATION_SESSION,
} from "~/common/constants/verificationCode";
import { customErrorSchema } from "~/common/types";
import { CodeVerificationSession } from "~/common/types/verificationCode";
import { useFormatHook } from "~/patients/hooks/useFormatHook";
import {
  useCancelVerificationCode,
  useRetryVerificationCode,
  useSendVerificationCode,
  useVerifyCode,
} from "~/providers/hooks";
import { useOnSignUpTeamMemberStore } from "~/providers/stores/useOnSignUpTeamMemberStore";
import { TeamMemberSignUpSchemaType } from "~/providers/utils/teamMemberSignUpSchema";
import { useSubmitTeamMemberSignUp } from "./useSubmitTeamMemberSignUp";

interface useSignUpNewTeamMemberProps {
  values: TeamMemberSignUpSchemaType;
  trigger: UseFormTrigger<TeamMemberSignUpSchemaType>;
  setError: UseFormSetError<TeamMemberSignUpSchemaType>;
  setValue: UseFormSetValue<TeamMemberSignUpSchemaType>;
  clearErrors: UseFormClearErrors<TeamMemberSignUpSchemaType>;
  inviteId?: string;
  token?: string;
}

export const useSignUpTeamMember = ({
  values,
  trigger,
  inviteId,
  setError,
  setValue,
  clearErrors,
  token,
}: useSignUpNewTeamMemberProps) => {
  const { stripPhoneNumber } = useFormatHook();
  const [verificationSession, setVerificationSession] =
    useState<CodeVerificationSession>(INITIAL_VERIFICATION_SESSION);

  const { mutate: sendVerificationCode, isPending: isSendingCode } =
    useSendVerificationCode();
  const { mutate: verifyCode, isPending: isVerifyingCode } = useVerifyCode();
  const { mutate: retryVerificationCode, isPending: isRetryingCode } =
    useRetryVerificationCode();
  const { mutate: cancelVerificationCode, isPending: isCancellingCode } =
    useCancelVerificationCode();

  const { isTeamMemberSignUpPending: isRegisteringTeamMember, onSubmitSignUp } =
    useSubmitTeamMemberSignUp({ values, trigger, inviteId, token });

  const { nextStep, previousStep, skipStep, setNewTeamMember, newTeamMember } =
    useOnSignUpTeamMemberStore();

  const isActionPending = [
    isRegisteringTeamMember,
    isSendingCode,
    isVerifyingCode,
    isRetryingCode,
    isCancellingCode,
  ].some(Boolean);

  const onPhoneNumberStepCompleted = () => {
    const selectedPhoneNumber = values?.mobileNumber?.phoneNumber;

    setNewTeamMember({
      ...newTeamMember,
      phoneNumber: selectedPhoneNumber,
    });

    sendVerificationCode(
      {
        phone_number: {
          number: stripPhoneNumber(selectedPhoneNumber),
          country_code: COUNTRY_CODES.US,
        },
        channel: CODE_VERIFICATION_CHANNELS.SMS,
        scope: CODE_VERIFICATION_SCOPES.SIGNUP,
      },
      {
        onSuccess: ({ verification_token, can_resend_at }) => {
          setVerificationSession((prev) => ({
            ...prev,
            canResendAt: new Date(can_resend_at).getTime(),
            token: verification_token,
            isExpired: false,
          }));
          nextStep();
        },
      },
    );
  };

  const onCodeVerificationStepCompleted = () => {
    if (!verificationSession.token) {
      setError("verification.code", {
        message: "Verification session expired. Please request a new code.",
      });
      return;
    }

    if (values.verification.code.length < DEFAULT_CODE_LENGTH) {
      setError("verification.code", {
        message: `${DEFAULT_CODE_LENGTH} character code is required`,
      });
      return;
    }

    verifyCode(
      {
        verification_code: values.verification.code,
        verification_token: verificationSession.token,
      },
      {
        onSuccess: ({ one_use_token }) => {
          setVerificationSession(INITIAL_VERIFICATION_SESSION);
          setNewTeamMember({
            ...newTeamMember,
            verificationToken: one_use_token,
          });
          nextStep();
        },
        onError: (error) => {
          const result = customErrorSchema.safeParse(error);
          if (result.success) {
            if (result.data.response.data.detail === "Token expired") {
              setError("verification.code", {
                message: "Code has expired, please try another one.",
              });
              setVerificationSession((prev) => ({
                ...prev,
                isExpired: true,
              }));
              return;
            }
          }
          setError("verification.code", {
            message: "This code doesn't match. Please try again.",
          });
        },
      },
    );
  };

  const onRetry = () => {
    const currentPhone = newTeamMember?.phoneNumber;

    if (!currentPhone) {
      setError("verification.code", {
        message: "No phone was provided",
      });
      return;
    }

    if (!verificationSession.token) {
      setError("verification.code", {
        message: "No verification token present.",
      });
      return;
    }

    const restartVerificationFlow = () => {
      if (!verificationSession.token) return;

      cancelVerificationCode(
        {
          verification_token: verificationSession.token,
        },
        {
          onSuccess: () => {
            sendVerificationCode(
              {
                phone_number: {
                  number: stripPhoneNumber(currentPhone),
                  country_code: COUNTRY_CODES.US,
                },
                scope: CODE_VERIFICATION_SCOPES.SIGNUP,
                channel: CODE_VERIFICATION_CHANNELS.SMS,
              },
              {
                onSuccess: ({ verification_token, can_resend_at }) => {
                  setVerificationSession((prev) => ({
                    ...prev,
                    token: verification_token,
                    canResendAt: new Date(can_resend_at).getTime(),
                    isExpired: false,
                  }));
                },
              },
            );
          },
        },
      );
    };

    if (verificationSession.isExpired) {
      restartVerificationFlow();
      return;
    }

    retryVerificationCode(
      { verification_token: verificationSession.token },
      {
        onSuccess: ({ can_resend_at, verification_token }) => {
          setVerificationSession((prev) => ({
            ...prev,
            canResendAt: new Date(can_resend_at).getTime(),
            token: verification_token,
            isExpired: false,
          }));
        },
        onError: (error) => {
          const result = customErrorSchema.safeParse(error);
          if (result.success) {
            if (result.data.response.data.detail === "Token expired") {
              restartVerificationFlow();
              return;
            }
            toast.error(result.data.response.data.detail);
          }
        },
      },
    );
  };

  const onBack = (schemaName: keyof TeamMemberSignUpSchemaType) => {
    if (verificationSession.token) {
      cancelVerificationCode(
        { verification_token: verificationSession.token },
        {
          onSuccess: () => {
            setVerificationSession(INITIAL_VERIFICATION_SESSION);
          },
        },
      );
    }
    if (schemaName === "password") {
      setValue("verification.code", "");
      skipStep(-2);
      return;
    }
    if (schemaName === "verification") {
      clearErrors("verification.code");
      previousStep();
      return;
    }
    previousStep();
  };

  const onNext = async (schemaName: keyof TeamMemberSignUpSchemaType) => {
    const isValid = await trigger(schemaName);
    if (isValid) {
      const signUpStepToAction: Record<string, () => void> = {
        mobileNumber: onPhoneNumberStepCompleted,
        verification: onCodeVerificationStepCompleted,
      };

      const runAction = signUpStepToAction[schemaName];

      if (runAction) {
        return runAction();
      }

      nextStep();
    }
  };

  return {
    onBack,
    onRetry,
    onNext,
    isActionPending,
    onSubmitSignUp,
    canResendAt: verificationSession.canResendAt ?? 0,
  };
};
